import React, { useState } from 'react';
import { Activity } from '../types';
import { FileTextIcon, SearchIcon, DownloadIcon, ExternalLinkIcon, UploadIcon } from 'lucide-react';
import { SummaryCard } from '../components/SummaryCard';
interface DocumentsPageProps {
  activities: Activity[];
  onViewActivity: (id: string) => void;
}
export function DocumentsPage({
  activities,
  onViewActivity
}: DocumentsPageProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('Semua Kategori');
  const [activityFilter, setActivityFilter] = useState('Semua Kegiatan');
  // Extract all documents from all activities
  const allDocuments = activities.flatMap(act => (act.documents || []).map(doc => ({
    ...doc,
    activityTitle: act.title,
    activityCode: act.code
  }))).sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime());
  const categories = ['Semua Kategori', ...Array.from(new Set(allDocuments.map(d => d.category)))];
  const activityCodes = ['Semua Kegiatan', ...Array.from(new Set(allDocuments.map(d => d.activityCode)))];
  const filteredDocs = allDocuments.filter(doc => {
    const matchSearch = doc.fileName.toLowerCase().includes(searchTerm.toLowerCase()) || doc.activityTitle.toLowerCase().includes(searchTerm.toLowerCase());
    const matchCategory = categoryFilter === 'Semua Kategori' || doc.category === categoryFilter;
    const matchActivity = activityFilter === 'Semua Kegiatan' || doc.activityCode === activityFilter;
    return matchSearch && matchCategory && matchActivity;
  });
  // Stats
  const totalDocs = allDocuments.length;
  const laporanDocs = allDocuments.filter(d => d.category === 'Laporan').length;
  const teknisDocs = allDocuments.filter(d => d.category === 'Teknis').length;
  const sprinDocs = allDocuments.filter(d => d.category === 'Surat Perintah').length;
  const handleUploadClick = () => {
    alert('Fitur upload dokumen akan tersedia pada versi berikutnya.');
  };
  return <div className="space-y-6 flex flex-col h-full" data-id="element-583">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-584">
        <h2 className="text-2xl font-bold text-[#E2E8F0] uppercase tracking-wide flex items-center" data-id="element-585">
          <FileTextIcon className="w-6 h-6 mr-3 text-[#00D4FF]" data-id="element-586" />
          DOKUMENTASI KEGIATAN
        </h2>
        <button onClick={handleUploadClick} className="flex items-center px-4 py-2 bg-[#00D4FF] text-[#0B0F1A] rounded hover:bg-[#00D4FF]/90 transition-colors shadow-sm font-bold text-sm uppercase glow-cyan" data-id="element-587">
          <UploadIcon className="w-4 h-4 mr-2" data-id="element-588" />
          UPLOAD DOKUMEN
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-589">
        <SummaryCard title="Total Dokumen" value={totalDocs} icon={<FileTextIcon className="w-6 h-6" data-id="element-591" />} iconBgColor="bg-[#00D4FF]/10" iconColor="text-[#00D4FF]" borderColor="border-t-[#00D4FF]" data-id="element-590" />
        <SummaryCard title="Laporan" value={laporanDocs} icon={<FileTextIcon className="w-6 h-6" data-id="element-593" />} iconBgColor="bg-[#00FF88]/10" iconColor="text-[#00FF88]" borderColor="border-t-[#00FF88]" data-id="element-592" />
        <SummaryCard title="Dokumen Teknis" value={teknisDocs} icon={<FileTextIcon className="w-6 h-6" data-id="element-595" />} iconBgColor="bg-[#0EA5E9]/10" iconColor="text-[#0EA5E9]" borderColor="border-t-[#0EA5E9]" data-id="element-594" />
        <SummaryCard title="Surat Perintah" value={sprinDocs} icon={<FileTextIcon className="w-6 h-6" data-id="element-597" />} iconBgColor="bg-[#FFB800]/10" iconColor="text-[#FFB800]" borderColor="border-t-[#FFB800]" data-id="element-596" />
      </div>

      <div className="bg-[#111827] p-4 rounded shadow-sm border border-[#1E3A5F] flex flex-wrap gap-4 items-center" data-id="element-598">
        <div className="relative flex-1 min-w-[250px]" data-id="element-599">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-600">
            <SearchIcon className="h-4 w-4 text-[#94A3B8]" data-id="element-601" />
          </div>
          <input type="text" placeholder="Cari nama dokumen atau kegiatan..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="block w-full pl-10 pr-3 py-2 border border-[#1E3A5F] rounded text-sm focus:ring-1 focus:ring-[#00D4FF] focus:border-[#00D4FF] bg-[#0B0F1A] text-[#E2E8F0] placeholder-[#475569]" data-id="element-602" />
        </div>
        <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="border border-[#1E3A5F] rounded text-sm py-2 pl-3 pr-8 focus:ring-1 focus:ring-[#00D4FF] focus:border-[#00D4FF] bg-[#0B0F1A] text-[#E2E8F0]" data-id="element-603">
          {categories.map(c => <option key={c} value={c} data-id="element-604">
              {c}
            </option>)}
        </select>
        <select value={activityFilter} onChange={e => setActivityFilter(e.target.value)} className="border border-[#1E3A5F] rounded text-sm py-2 pl-3 pr-8 focus:ring-1 focus:ring-[#00D4FF] focus:border-[#00D4FF] bg-[#0B0F1A] text-[#E2E8F0] max-w-[250px] truncate" data-id="element-605">
          {activityCodes.map(a => <option key={a} value={a} data-id="element-606">
              {a}
            </option>)}
        </select>
      </div>

      <div className="bg-[#111827] rounded shadow-lg border border-[#1E3A5F] overflow-hidden flex-1" data-id="element-607">
        <div className="overflow-x-auto" data-id="element-608">
          <table className="w-full text-sm text-left" data-id="element-609">
            <thead className="text-xs text-[#94A3B8] uppercase bg-[#0F1629] border-b border-[#1E3A5F]" data-id="element-610">
              <tr data-id="element-611">
                <th className="px-4 py-3 font-semibold w-12 text-center" data-id="element-612">NO</th>
                <th className="px-4 py-3 font-semibold" data-id="element-613">NAMA DOKUMEN</th>
                <th className="px-4 py-3 font-semibold" data-id="element-614">KATEGORI</th>
                <th className="px-4 py-3 font-semibold" data-id="element-615">KEGIATAN TERKAIT</th>
                <th className="px-4 py-3 font-semibold" data-id="element-616">DIUNGGAH OLEH</th>
                <th className="px-4 py-3 font-semibold" data-id="element-617">WAKTU</th>
                <th className="px-4 py-3 font-semibold text-center" data-id="element-618">AKSI</th>
              </tr>
            </thead>
            <tbody data-id="element-619">
              {filteredDocs.length > 0 ? filteredDocs.map((doc, index) => <tr key={doc.id} className="border-b border-[#1E3A5F]/50 hover:bg-[#1A2332] transition-colors" data-id="element-620">
                    <td className="px-4 py-4 text-center text-[#94A3B8] text-sm" data-id="element-621">
                      {index + 1}
                    </td>
                    <td className="px-4 py-4" data-id="element-622">
                      <div className="flex items-center space-x-2" data-id="element-623">
                        <FileTextIcon className="w-4 h-4 text-[#FF3B3B]" data-id="element-624" />
                        <span className="font-medium text-[#E2E8F0] text-sm" data-id="element-625">
                          {doc.fileName}
                        </span>
                      </div>
                      <div className="text-xs text-[#94A3B8] mt-1" data-id="element-626">
                        {doc.fileSize} • {doc.mimeType}
                      </div>
                    </td>
                    <td className="px-4 py-4" data-id="element-627">
                      <span className="bg-[#0EA5E9]/10 text-[#0EA5E9] border border-[#0EA5E9]/20 text-xs px-2 py-0.5 rounded" data-id="element-628">
                        {doc.category}
                      </span>
                    </td>
                    <td className="px-4 py-4" data-id="element-629">
                      <div className="font-mono text-[#00D4FF] text-xs mb-1" data-id="element-630">
                        {doc.activityCode}
                      </div>
                      <div className="text-sm text-[#94A3B8] truncate max-w-[200px]" title={doc.activityTitle} data-id="element-631">
                        {doc.activityTitle}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-[#E2E8F0]" data-id="element-632">
                      {doc.uploadedBy}
                    </td>
                    <td className="px-4 py-4 font-mono text-xs text-[#94A3B8]" data-id="element-633">
                      {new Date(doc.uploadedAt).toLocaleString('id-ID')}
                    </td>
                    <td className="px-4 py-4" data-id="element-634">
                      <div className="flex items-center justify-center space-x-2" data-id="element-635">
                        <button className="p-1.5 text-[#00D4FF]/60 hover:text-[#00D4FF] hover:bg-[#00D4FF]/10 rounded transition-colors" title="Unduh" data-id="element-636">
                          <DownloadIcon className="w-4 h-4" data-id="element-637" />
                        </button>
                        <button onClick={() => onViewActivity(doc.activityId)} className="p-1.5 text-[#94A3B8] hover:text-[#E2E8F0] hover:bg-[#1E3A5F] rounded transition-colors" title="Lihat Kegiatan" data-id="element-638">
                          <ExternalLinkIcon className="w-4 h-4" data-id="element-639" />
                        </button>
                      </div>
                    </td>
                  </tr>) : <tr data-id="element-640">
                  <td colSpan={7} className="px-4 py-8 text-center text-[#94A3B8] text-sm" data-id="element-641">
                    Tidak ada dokumen yang ditemukan.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}